// Backup & Restore utilities
import { DataStore } from './data-store';

export interface BackupData {
  version: string;
  timestamp: string;
  data: any;
}

export const createFullBackup = (): BackupData => {
  const data = DataStore.getData();
  return {
    version: '1.0.0',
    timestamp: new Date().toISOString(),
    data,
  };
};

export const downloadBackup = () => {
  const backup = createFullBackup();
  const filename = `sap-ngecat-backup-${new Date().getTime()}.json`;
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
  link.download = filename;
  link.click();
  
  return filename;
};

export const restoreBackup = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const backup = JSON.parse(event.target?.result as string) as BackupData;
        
        // Validate backup structure
        if (!backup.data || !backup.version) {
          throw new Error('Invalid backup file format');
        }
        
        // Restore data
        DataStore.saveData(backup.data);
        resolve(true);
      } catch (error) {
        console.error('Restore error:', error);
        resolve(false);
      }
    };
    
    reader.readAsText(file);
  });
};

export const getBackupInfo = (): string => {
  const backup = createFullBackup();
  const data = backup.data;
  
  const info = `
SAP Ngecat Backup Report
========================
Timestamp: ${backup.timestamp}
Version: ${backup.version}

Data Summary:
- Customers: ${data.customers?.length || 0}
- Projects: ${data.projects?.length || 0}
- Invoices: ${data.invoices?.length || 0}
- Quotes: ${data.quotes?.length || 0}
- Site Visits: ${data.siteVisits?.length || 0}
- Costs: ${data.costs?.length || 0}
- Reports: ${data.reports?.length || 0}
- Project Images: ${data.projectImages?.length || 0}

Total Records: ${
    (data.customers?.length || 0) +
    (data.projects?.length || 0) +
    (data.invoices?.length || 0) +
    (data.quotes?.length || 0) +
    (data.siteVisits?.length || 0) +
    (data.costs?.length || 0) +
    (data.reports?.length || 0) +
    (data.projectImages?.length || 0)
  }
  `;
  
  return info;
};

export const calculateBackupSize = (): string => {
  const backup = createFullBackup();
  const sizeInBytes = new Blob([JSON.stringify(backup)]).size;
  
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  } else if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

export const getLastBackupTime = (): string => {
  const backup = createFullBackup();
  const date = new Date(backup.timestamp);
  return date.toLocaleDateString('ms-MY', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
